/**
 * On Board — 폼값 → 템플릿 [[변수]] 최종 변환.
 *   - 반복부(loop) flatten: 화면의 `${prefix}_count` + `${prefix}_${i}_${field}` → 배열.
 *       appt_dir → appointed_directors, resign_dir → resigned_directors,
 *       appt_sec → appointed_secretaries, resign_sec → resigned_secretaries,
 *       bnd_dir → directors, bnd_ubo → ubos, bnd_sh → shareholders.
 *   - 지명 서명필드([[*_signature]])는 T-Sign 단계에서 채워지므로 빈 문자열로 방출(미치환 방지).
 *   - 체크박스/날짜는 화면 값 그대로(백엔드가 ISO→세션 형식 변환).
 */

type Row = Record<string, string>;

function collectLoop(input: Record<string, any>, prefix: string, fields: string[]): Row[] {
  const cnt = parseInt(String(input[`${prefix}_count`] ?? '0'), 10);
  const out: Row[] = [];
  if (!Number.isFinite(cnt) || cnt <= 0) return out;
  for (let i = 1; i <= cnt; i++) {
    const row: Row = {};
    let any = false;
    for (const f of fields) {
      const v = String(input[`${prefix}_${i}_${f}`] ?? '').trim();
      row[f] = v;
      if (v) any = true;
    }
    if (any) out.push(row);
  }
  return out;
}

/** 지명 서명 변수 — 항상 빈값이라도 방출해 [[*_signature]] 미치환을 막는다. */
const SIGNATURE_KEYS = [
  'director_signature',
  'aura_signatory_signature',
  'principal_signature',
  'company_witness_signature',
  'principal_witness_signature',
  'serviceprovider_witness_signature',
  'authorised_person_witness_signature',
  'witness_signature',
];

export function normalizeOnBoardForWord(input: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...input };

  // ── 반복부 flatten ──
  //   선임 = Form 45/45B 연동 행(appt_*) + DR 화면에서 직접 추가한 행(appt_*_m).
  const DIR_APPT = ['director_name', 'director_identification_no', 'effective_date'];
  const SEC_APPT = ['secretary_name', 'secretary_identification_no', 'his_her'];
  out.appointed_directors = [...collectLoop(input, 'appt_dir', DIR_APPT), ...collectLoop(input, 'appt_dir_m', DIR_APPT)];
  out.resigned_directors = collectLoop(input, 'resign_dir', ['director_name', 'director_identification_no', 'effective_date']);
  out.appointed_secretaries = [...collectLoop(input, 'appt_sec', SEC_APPT), ...collectLoop(input, 'appt_sec_m', SEC_APPT)];
  out.resigned_secretaries = collectLoop(input, 'resign_sec', ['secretary_name', 'secretary_identification_no', 'effective_date']);
  out.directors = collectLoop(input, 'bnd_dir', ['director_name', 'director_identification_no', 'director_address', 'director_mobile_no', 'director_email']);
  out.ubos = collectLoop(input, 'bnd_ubo', ['ubo_name', 'ubo_identification_no', 'ubo_residential_address']);
  out.shareholders = collectLoop(input, 'bnd_sh', ['shareholder_name', 'shareholder_identification_no', 'shareholder_address']);

  // ── 섹션 표시 조건 플래그(반복부 0건이면 헤더/문구 숨김) ──
  out.has_appointed_directors = out.appointed_directors.length ? 'yes' : 'no';
  out.has_resigned_directors = out.resigned_directors.length ? 'yes' : 'no';
  out.has_appointed_secretaries = out.appointed_secretaries.length ? 'yes' : 'no';
  out.has_resigned_secretaries = out.resigned_secretaries.length ? 'yes' : 'no';

  // ── Authorised Person "SIGNED by" — 고객 수정본(2026-10)에서 [[ubo_name]] → [[authorised_person_name]].
  //    입력이 없으면 예전처럼 Principal 이름으로.
  if (!String(out.authorised_person_name ?? '').trim()) out.authorised_person_name = String(out.ubo_name ?? '');

  // ── 지명 서명필드 빈값 보장 ──
  for (const k of SIGNATURE_KEYS) if (!(k in out) || out[k] == null) out[k] = '';

  return out;
}

/**
 * 회사 단위 문서(번들·DR) 전용 — 회사측 서명 이사 [[director_name]] 이 비면 Schedule 1 첫 이사로,
 * 직책이 비면 'Director' 로. (번들의 서비스계약 page 4 · Nomination Letter · Letter of Indemnity ·
 * 등록부 선언서 · Nominee Director 계약 서명란이 전부 이 키 하나를 쓴다.)
 * ⚠ Form 45(이사별 문서)에는 쓰지 않는다 — 그 director_name 은 새로 선임되는 사람이다.
 */
export function withCompanySignerFallback(out: Record<string, any>): Record<string, any> {
  const firstDir = Array.isArray(out.directors) ? out.directors[0] : null;
  if (!String(out.director_name ?? '').trim() && firstDir?.director_name) {
    out.director_name = firstDir.director_name;
    if (!String(out.director_identification_no ?? '').trim()) out.director_identification_no = firstDir.director_identification_no;
  }
  if (!String(out.director_designation ?? '').trim()) out.director_designation = 'Director';
  return out;
}
